import { useState, type FormEvent } from 'react';
import {
  INSTAGRAM_URL,
  CONTACT_EMAIL,
  STARTING_PRICE,
  LEAD_TIME,
  FULFILLMENT,
  FLAVORS,
} from '../../config/site';

function InstagramIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
      <rect x="2" y="2" width="20" height="20" rx="5" />
      <circle cx="12" cy="12" r="5" />
      <circle cx="18" cy="6" r="1.5" fill="currentColor" stroke="none" />
    </svg>
  );
}

const STEPS = [
  { n: '01', t: 'MESSAGE US', d: 'Send your date, the letters or number, and a flavor.' },
  { n: '02', t: 'WE CONFIRM & QUOTE', d: 'We reply with details, pricing, and availability.' },
  { n: '03', t: 'PICKUP OR DELIVERY', d: 'Collect in SF, or we deliver across the Bay 9–11pm.' },
];

const inputClass =
  'w-full border border-gray-300 px-4 py-3 font-inter text-xs tracking-[0.05em] text-text-primary placeholder:text-text-muted/50 focus:outline-none focus:border-text-primary transition-colors bg-white';

export default function OrderSection() {
  const [form, setForm] = useState({
    name: '',
    contact: '',
    date: '',
    text: '',
    flavor: FLAVORS[0].name,
    fulfillment: 'Pickup (SF)',
    message: '',
  });

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    // No backend yet: open the customer's mail client with the details prefilled.
    // TODO: swap for a form service (Formspree) or a serverless endpoint that emails the shop.
    const subject = `Cake order — ${form.text || 'inquiry'} (${form.date || 'date TBD'})`;
    const body = [
      `Name: ${form.name}`,
      `Contact: ${form.contact}`,
      `Event date: ${form.date}`,
      `Letters / number: ${form.text}`,
      `Flavor: ${form.flavor}`,
      `Pickup or delivery: ${form.fulfillment}`,
      '',
      form.message,
    ].join('\n');
    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
      subject,
    )}&body=${encodeURIComponent(body)}`;
  };

  return (
    <section id="order" className="py-16 md:py-24 bg-white scroll-mt-[132px]">
      <div className="max-w-3xl mx-auto px-6">
        <div className="text-center mb-12">
          <p className="font-inter text-xs tracking-[0.3em] uppercase text-text-muted mb-3">
            {STARTING_PRICE} · {FULFILLMENT}
          </p>
          <h2 className="font-inter text-sm md:text-base font-medium tracking-[0.3em] uppercase text-text-primary">
            HOW TO ORDER
          </h2>
        </div>

        {/* 3-step path */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8 mb-14 text-center">
          {STEPS.map((s) => (
            <div key={s.n}>
              <p className="font-display text-4xl text-sage-dark mb-3">{s.n}</p>
              <h3 className="font-inter text-xs font-medium tracking-[0.2em] uppercase text-text-primary mb-2">
                {s.t}
              </h3>
              <p className="font-inter text-sm leading-relaxed text-text-muted max-w-xs mx-auto">
                {s.d}
              </p>
            </div>
          ))}
        </div>

        {/* Primary CTA: Instagram */}
        <div className="text-center mb-12">
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-theme-font text-white font-inter text-xs tracking-[0.25em] uppercase px-8 py-4 rounded-xl no-underline hover:opacity-90 transition-opacity"
          >
            <InstagramIcon />
            Order on Instagram
          </a>
          <p className="font-inter text-xs text-text-muted mt-3">{LEAD_TIME}</p>
        </div>

        <p className="text-center font-inter text-[11px] tracking-[0.2em] uppercase text-text-muted/60 mb-8">
          — or send us the details —
        </p>

        {/* Inquiry form (mailto fallback for non-Instagram visitors) */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input className={inputClass} placeholder="Your name" value={form.name} onChange={set('name')} required aria-label="Your name" />
            <input className={inputClass} placeholder="Email or phone" value={form.contact} onChange={set('contact')} required aria-label="Email or phone" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              className={`${inputClass} [&::-webkit-date-and-time-value]:text-left [&::-webkit-date-and-time-value]:w-full`}
              type="date"
              value={form.date}
              onChange={set('date')}
              required
              aria-label="Event date"
            />
            <input className={inputClass} placeholder="Letters or number (e.g. K, or 30)" value={form.text} onChange={set('text')} required aria-label="Letters or number" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <select className={inputClass} value={form.flavor} onChange={set('flavor')} aria-label="Flavor">
              {FLAVORS.map((f) => (
                <option key={f.id} value={f.name}>
                  {f.name.charAt(0) + f.name.slice(1).toLowerCase()}
                </option>
              ))}
            </select>
            <select className={inputClass} value={form.fulfillment} onChange={set('fulfillment')} aria-label="Pickup or delivery">
              <option>Pickup (SF)</option>
              <option>Delivery (Bay Area, 9–11pm)</option>
            </select>
          </div>
          <textarea
            className={`${inputClass} resize-none`}
            rows={3}
            placeholder="Anything else? Theme, colors, allergies…"
            value={form.message}
            onChange={set('message')}
            aria-label="Message"
          />
          <div className="text-center pt-2">
            <button
              type="submit"
              className="font-inter text-xs tracking-[0.2em] uppercase text-text-primary bg-transparent border-0 cursor-pointer pb-1 border-b-2 border-sage hover:opacity-80 transition-opacity"
            >
              SEND REQUEST
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
